import { useCallback, useEffect, useState } from "react";
import { ActivityType, User } from "../types/types";
import { getApiUrlFromStorage, getTokenFromStorage, } from "../helpers/helpers";
import { AuthContext } from "../contexts/AuthContext";

const AuthProvider = ({ children }: { children: JSX.Element | JSX.Element[] }) => {

  const [userData, setUserData] = useState<User>();
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [updateContext, setUpdateContext] = useState(false);

  const authToken = getTokenFromStorage();
  const apiUrl = getApiUrlFromStorage();

  const fetchLoggedInUser = useCallback(async () => {
    if (!apiUrl || !authToken) {
      return;
    }

    setIsLoading(true);
    fetch(`${apiUrl}/users/me`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    })
      .then(res => {
        if (!res.ok) {
          throw Error('Could not fetch user data');
        }
        return res.json();
      })
      .then((data) => {
        setUserData(data);
      })
      .catch((err) => {
        // catches network errors + http not OK errors
        console.log("Unable to fetch user data");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authToken, apiUrl]);

  const fetchActivityTypes = useCallback(async () => {
    if (!apiUrl || !authToken) {
      return;
    }
    setIsLoading(true);
    fetch(`${apiUrl}/activity-types?sort=name`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    })
      .then(res => {
        if (!res.ok) {
          throw Error('Could not fetch activity types');
        }
        return res.json();
      })
      .then((data) => {
        setActivityTypes(data.data);
      })
      .catch((err) => {
        // catches network errors + http not OK errors
        console.log("Unable to fetch activity types");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authToken, apiUrl]);


  const handleUpdateContext = () => {
    setUpdateContext(!updateContext);
  }

  useEffect(() => {
    if (authToken && apiUrl) {
      fetchLoggedInUser();
      fetchActivityTypes();
    }
  }, [authToken, apiUrl, fetchLoggedInUser, fetchActivityTypes]);

  return (
    <AuthContext.Provider
      value={
        {
          user: userData,
          setUser: setUserData,
          isLoading: isLoading,
          activityTypes: activityTypes,
          updateContext: handleUpdateContext,
        }
      }>
      {children}
    </AuthContext.Provider>
  );

};

export default AuthProvider;